import dialogManager, { DIALOG_NAMES } from '../managers/DialogManager';
import contentManager from '../managers/ContentManager';
import classroomManager from '../managers/ClassroomManager';

/**
 * Opens Presentation Options after a teacher presentation closes.
 * Guarded so hideIframe + closePressed do not open the modal twice.
 */
class PresentationOptionsService {
  static _openPending = false;

  static isOpenPending = () => this._openPending;

  static resetOpenPending = () => {
    this._openPending = false;
  };

  /**
   * @param {object} params
   * @param {string} [params.activityId]
   * @param {string} [params.activityInstanceId]
   * @param {string} [params.assignmentId]
   * @param {string} [params.classroomId]
   * @param {boolean} [params.wasPresenting] - explicit flag; otherwise uses contentManager.isTeacherPresenting
   * @param {boolean} [params.useDialogManager=true] - false for PlayerLaunch (no DialogList)
   * @param {function} [params.onBackgroundRefresh] - called when duplicate/assign success is shown
   * @param {function} [params.onLocalOpen] - called when useDialogManager is false
   * @returns {boolean} whether the modal was opened
   */
  static openIfPresenting = ({
    activityId = null,
    activityInstanceId = null,
    assignmentId = null,
    classroomId = null,
    wasPresenting = null,
    useDialogManager = true,
    onBackgroundRefresh = null,
    onLocalOpen = null
  } = {}) => {
    const presenting = typeof wasPresenting === 'boolean'
      ? wasPresenting
      : contentManager.isTeacherPresenting;

    if (!presenting || this._openPending) {
      return false;
    }

    this._openPending = true;

    const storedContext = contentManager.presentationContext || {};
    const context = {
      activityId: activityId || storedContext.activityId || null,
      activityInstanceId: activityInstanceId || storedContext.activityInstanceId || null,
      assignmentId: assignmentId || storedContext.assignmentId
        || activityId || storedContext.activityId || null,
      classroomId: classroomId || storedContext.classroomId
        || classroomManager.currentClassroomId || null
    };

    contentManager.setPresentationContext(context);

    if (useDialogManager) {
      dialogManager.setOpenDialog(DIALOG_NAMES.PRESENTATION_OPTIONS, {
        ...context,
        onBackgroundRefresh,
        closePresentationOptions: () => {
          dialogManager.closeDialog(DIALOG_NAMES.PRESENTATION_OPTIONS);
          this.resetOpenPending();
          contentManager.clearPresentationContext();
          contentManager.setIsTeacherPresenting(false);
        }
      }, () => {
        this.resetOpenPending();
        contentManager.clearPresentationContext();
        contentManager.setIsTeacherPresenting(false);
      });
      return true;
    }

    if (typeof onLocalOpen === 'function') {
      onLocalOpen(context);
      return true;
    }

    this.resetOpenPending();
    return false;
  };
}

export default PresentationOptionsService;
